<template>
    <div class="user-tabs-container">
        <div v-if="showLeftArrow" class="scroll-arrow scroll-arrow-left" @click="scrollLeft">
            <i class="fas fa-angle-left"></i>
        </div>

        <div class="tab-bar mx-n2" style="min-height: 60px; position: relative;">
            <div ref="scrollContainer" class="user-navigation overflow-auto d-flex align-items-center text-nowrap pt-2" @scroll="updateArrows">
                <button
                    v-for="consultant in consultants"
                    class="btn btn-sm mx-1 flex-shrink-0 d-flex align-items-center gap-1"
                    :key="consultant.id"
                    :class="{
                        'btn-consultant-selected': selectedUserId === consultant.id,
                        'btn-consultant': selectedUserId !== consultant.id
                    }"
                    @click="selectUser(consultant.id)"
                >
                    <span>{{ consultant.name }}</span>

                    <span v-if="consultant.card_count > 0" class="btn-consultant-badge">
                        {{ consultant.card_count }}
                    </span>
                </button>
            </div>
        </div>

        <div v-if="showRightArrow" class="scroll-arrow scroll-arrow-right" @click="scrollRight">
            <i class="fas fa-angle-right"></i>
        </div>
    </div>
</template>

<script>
import { getConsultants } from '../../../services/api/to-do';

export default {
    name: 'ToDoUserTabs',
    props: {
        roleId: {
            type: Number,
            required: true
        },
        userId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            consultants: [],
            isLoadingConsultants: false,
            selectedUserId: null,
            showLeftArrow: false,
            showRightArrow: false
        };
    },
    mounted() {
        this.getMyConsultants();
        this.$nextTick(() => {
            this.updateArrows();
            if (this.$refs.scrollContainer) {
                window.addEventListener('resize', this.updateArrows);
                this.$refs.scrollContainer.addEventListener('wheel', this.handleMouseWheel);
            }
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateArrows);

        if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.removeEventListener('wheel', this.handleMouseWheel);
        }
    },
    methods: {
        async getMyConsultants() {
            this.isLoadingConsultants = true;

            try {
                const response = await getConsultants(this.userId);

                if (response.status === 200) {
                    this.consultants = response.data.data
                        .map(user => ({
                            id: user.id,
                            name: user.name,
                            card_count: user.card_count ?? 0
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));

                    if (this.consultants.length > 0) {
                        const previouslySelected = this.selectedUserId;
                        this.selectedUserId = this.selectedUserId ?? this.consultants[0].id;

                        if (this.selectedUserId !== previouslySelected) {
                            this.$emit('user-selected', this.selectedUserId);
                        }
                    }

                    this.$nextTick(() => this.updateArrows());
                }
            } catch (error) {
                console.error('Error fetching consultant list:', error);
            } finally {
                this.isLoadingConsultants = false;
            }
        },
        selectUser(id) {
            if (id !== this.selectedUserId) {
                this.selectedUserId = id;
                this.$emit('user-selected', id);
            }
        },
        updateArrows() {
            const container = this.$refs.scrollContainer;

            if (!container) {
                return;
            }

            const scrollLeft = container.scrollLeft;
            const scrollWidth = container.scrollWidth;
            const clientWidth = container.clientWidth;

            this.showLeftArrow = scrollLeft > 1;
            this.showRightArrow = (scrollLeft + clientWidth) < (scrollWidth - 2); // Add 2px tolerance to handle subpixel precision
        },
        scrollLeft() {
            const container = this.$refs.scrollContainer;
            if (!container) {
                return;
            }

            const scrollAmount = Math.min(200, container.scrollLeft);
            container.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });
        },
        scrollRight() {
            const container = this.$refs.scrollContainer;
            if (!container) {
                return;
            }

            const maxScroll = container.scrollWidth - container.clientWidth;
            const currentScroll = container.scrollLeft;
            const scrollAmount = Math.min(200, maxScroll - currentScroll);
            
            container.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        },
        handleMouseWheel(event) {
            const container = this.$refs.scrollContainer;
            if (!container) {
                return;
            }

            // Prevent vertical scrolling if it's a horizontal scroll
            if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                event.preventDefault();
            }

            // Scroll horizontally
            container.scrollLeft += event.deltaY > 0 ? 100 : -100;
            this.updateArrows();
        }
    }
};
</script>
